<template>
  <div class="eni-app-header">
    <!-- 顶部背景装饰 -->
    <div class="eni-header-grid"></div>
    <div class="eni-header-glow eni-header-glow-gold"></div>
    <div class="eni-header-glow eni-header-glow-blue"></div>
    <div class="eni-header-scan-line"></div>

    <!-- 左侧品牌 -->
    <div
      class="eni-header-left"
      role="button"
      aria-label="返回首页"
      @click="goHome"
    >
      <div class="eni-brand-shell">
        <!-- LOGO 外围动态轨道 -->
        <span class="eni-brand-orbit"></span>
        <span class="eni-brand-orbit eni-brand-orbit-secondary"></span>

        <div class="eni-brand-logo-box">
          <span class="eni-brand-inner-glow"></span>
          <span class="eni-brand-shine"></span>

          <img
            class="eni-brand-logo"
            :src="logoImg"
            alt="BCH Logo"
          />
        </div>

        <!-- 在线状态灯 -->
        <span class="eni-brand-live">
          <i></i>
        </span>
      </div>
    </div>

    <!-- 中间钱包 -->
    <div class="eni-header-center">
      <div
        class="eni-wallet-pill"
        :class="{ connected: isConnected }"
        role="button"
        :aria-label="walletDisplayText"
        @click="openWalletDrawer"
      >
        <span class="eni-wallet-shine"></span>

        <div class="eni-wallet-logo-wrap">
          <span class="eni-wallet-ring"></span>

          <img
            v-if="isConnected && currentWalletIcon"
            :src="currentWalletIcon"
            class="eni-wallet-logo"
            alt="wallet"
          />

          <div v-else class="eni-wallet-badge">
            <span>+</span>
          </div>

          <span
            v-if="isConnected"
            class="eni-wallet-online-dot"
          ></span>
        </div>

        <div class="eni-wallet-content">
          <span class="eni-wallet-label">
            {{ isConnected ? "CONNECTED" : "WEB3 WALLET" }}
          </span>

          <span class="eni-wallet-text">
            {{ walletDisplayText }}
          </span>
        </div>

        <svg
          viewBox="0 0 24 24"
          class="eni-wallet-arrow"
          aria-hidden="true"
        >
          <path d="M9 6L15 12L9 18"></path>
        </svg>
      </div>
    </div>

    <!-- 右侧菜单 -->
    <div class="eni-header-right">
      <button
        class="eni-menu-btn"
        type="button"
        aria-label="打开菜单"
        @click="menuPopup = true"
      >
        <span class="eni-menu-btn-glow"></span>

        <svg
          viewBox="0 0 24 24"
          class="eni-menu-svg"
          aria-hidden="true"
        >
          <path d="M4 7H20"></path>
          <path d="M8 12H20"></path>
          <path d="M12 17H20"></path>
        </svg>
      </button>
    </div>

    <!-- 钱包抽屉 -->
    <header-wallet-drawer
      :visible.sync="walletPopup"
      @select="handleWalletSelect"
      @success="handleWalletSuccess"
    />

    <!-- 菜单抽屉 -->
    <header-menu-drawer
      :visible.sync="menuPopup"
      :logo-img="logoImg"
      :nav-list="navList"
      :current-lang-code="currentLangCode"
      @navigate="handleNav"
      @change-lang="onSelectLang"
    />
  </div>
</template>

<script>
import logoImg from "@/assets/img/logo.png";
import HeaderWalletDrawer from "./HeaderWalletDrawer.vue";
import HeaderMenuDrawer from "./HeaderMenuDrawer.vue";
import wallet from "@/store/wallet";
import { getLocaleMeta } from "@/i18n/localeMeta";

export default {
  name: "AppHeader",

  components: {
    HeaderWalletDrawer,
    HeaderMenuDrawer
  },

  data() {
    return {
      logoImg,
      wallet,

      walletPopup: false,
      menuPopup: false,

      currentWalletTag:
        localStorage.getItem("eni_wallet_tag") || "",

      currentWalletName:
        localStorage.getItem("eni_wallet_name") || "",

      currentWalletIcon:
        localStorage.getItem("eni_wallet_icon") || ""
    };
  },

  computed: {
    currentAddress() {
      return this.normalizeAddress(
        this.wallet?.state?.address || ""
      );
    },

    isConnected() {
      return (
        !!this.wallet?.state?.connected &&
        !!this.currentAddress
      );
    },

    walletDisplayText() {
      return this.isConnected
        ? this.shortAddress(this.currentAddress)
        : this.$t("wallet.connect");
    },

    currentLangCode() {
      return this.wallet?.state?.lang || "en";
    },

    currentLangMeta() {
      return getLocaleMeta(this.currentLangCode);
    },

    navList() {
      return [
        {
          key: "home",
          name: this.$t("nav.home"),
          path: "/"
        },
        {
          key: "participate",
          name: this.$t("nav.market"),
          path: "/market"
        },
        {
          key: "team",
          name: this.$t("nav.team"),
          path: "/team"
        },
        {
          key: "income",
          name: this.$t("nav.income"),
          path: "/income"
        }
      ];
    }
  },

  created() {
    this.initWalletData();

    window.addEventListener(
      "storage",
      this.handleStorageSync
    );
  },

  beforeDestroy() {
    window.removeEventListener(
      "storage",
      this.handleStorageSync
    );
  },

  methods: {
    initWalletData() {
      this.currentWalletTag =
        localStorage.getItem("eni_wallet_tag") || "";

      this.currentWalletName =
        localStorage.getItem("eni_wallet_name") || "";

      this.currentWalletIcon =
        localStorage.getItem("eni_wallet_icon") || "";
    },

    normalizeAddress(address) {
      const val = String(address || "").trim();

      if (
        !val ||
        val === "Not Connected" ||
        val === "undefined" ||
        val === "null"
      ) {
        return "";
      }

      return val;
    },

    shortAddress(address) {
      const val = this.normalizeAddress(address);

      if (!val) {
        return this.$t("wallet.connect");
      }

      if (val.length <= 12) {
        return val;
      }

      return val.slice(0, 6) + "..." + val.slice(-4);
    },

    openWalletDrawer() {
      this.walletPopup = true;
    },

    handleWalletSelect(item) {
      this.currentWalletTag = item?.key || "";
      this.currentWalletName = item?.name || "";
      this.currentWalletIcon = item?.icon || "";

      localStorage.setItem(
        "eni_wallet_tag",
        this.currentWalletTag
      );

      localStorage.setItem(
        "eni_wallet_name",
        this.currentWalletName
      );

      localStorage.setItem(
        "eni_wallet_icon",
        this.currentWalletIcon
      );
    },

    handleWalletSuccess(payload) {
      const item = payload?.wallet || {};

      this.currentWalletTag =
        item.key ||
        this.currentWalletTag ||
        "";

      this.currentWalletName =
        item.name ||
        this.currentWalletName ||
        "";

      this.currentWalletIcon =
        item.icon ||
        this.currentWalletIcon ||
        "";

      localStorage.setItem(
        "eni_wallet_tag",
        this.currentWalletTag
      );

      localStorage.setItem(
        "eni_wallet_name",
        this.currentWalletName
      );

      localStorage.setItem(
        "eni_wallet_icon",
        this.currentWalletIcon
      );

      this.walletPopup = false;
    },

    handleStorageSync(e) {
      if (!e) return;

      if (e.key === "eni_wallet_tag") {
        this.currentWalletTag = e.newValue || "";
      }

      if (e.key === "eni_wallet_name") {
        this.currentWalletName = e.newValue || "";
      }

      if (e.key === "eni_wallet_icon") {
        this.currentWalletIcon = e.newValue || "";
      }
    },

    clearWalletData() {
      this.currentWalletTag = "";
      this.currentWalletName = "";
      this.currentWalletIcon = "";

      localStorage.removeItem("eni_wallet_tag");
      localStorage.removeItem("eni_wallet_name");
      localStorage.removeItem("eni_wallet_icon");
    },

    handleNav(item) {
      this.menuPopup = false;

      if (
        item.path &&
        this.$route.path !== item.path
      ) {
        this.$router.push(item.path);
      }
    },

    onSelectLang(item) {
      const code = item?.code || "en";

      this.wallet.setLang(code);

      this.$nextTick(() => {
        this.menuPopup = false;
      });

      if (this.$toast) {
        this.$toast.success({
          title: this.$t("common.success"),
          desc: this.$t(
            "common.languageSwitched",
            {
              lang: item?.name || code
            }
          )
        });
      }
    },

    goHome() {
      if (this.$route.path !== "/") {
        this.$router.push("/");
      }
    }
  }
};
</script>

<style scoped lang="less">
/* =========================================================
 * BCH APP HEADER
 * 深蓝黑背景 + 金色高光 + 科技蓝
 * ========================================================= */

.eni-app-header {
  position: sticky;
  top: 0;
  z-index: 1200;

  min-height: 78px;
  padding:
    calc(8px + env(safe-area-inset-top))
    12px
    8px;

  display: grid;

  /* 左侧 LOGO 区域加宽 */
  grid-template-columns: 112px minmax(0, 1fr) 46px;

  align-items: center;
  column-gap: 9px;

  overflow: hidden;
  isolation: isolate;
  box-sizing: border-box;

  background:
    radial-gradient(
      circle at 10% 0%,
      rgba(212, 175, 55, 0.16),
      transparent 30%
    ),
    radial-gradient(
      circle at 92% 10%,
      rgba(37, 99, 235, 0.18),
      transparent 34%
    ),
    linear-gradient(
      180deg,
      rgba(5, 7, 13, 0.98) 0%,
      rgba(7, 13, 24, 0.96) 65%,
      rgba(8, 15, 28, 0.93) 100%
    );

  border-bottom:
    1px solid rgba(255, 224, 138, 0.12);

  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.24),
    inset 0 -1px 0 rgba(59, 130, 246, 0.05);

  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* 顶部底部渐变线 */
.eni-app-header::after {
  content: "";
  position: absolute;
  z-index: 1;
  left: 10%;
  right: 10%;
  bottom: 0;
  height: 1px;

  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(59, 130, 246, 0.34) 25%,
    rgba(255, 224, 138, 0.58) 50%,
    rgba(59, 130, 246, 0.34) 75%,
    transparent 100%
  );

  opacity: 0.7;
}

/* 背景网格 */
.eni-header-grid {
  position: absolute;
  z-index: -3;
  inset: 0;

  background-image:
    linear-gradient(
      rgba(255, 255, 255, 0.022) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.022) 1px,
      transparent 1px
    );

  background-size: 24px 24px;
  opacity: 0.7;
  pointer-events: none;
}

/* 背景光晕 */
.eni-header-glow {
  position: absolute;
  z-index: -2;

  width: 130px;
  height: 130px;

  border-radius: 50%;
  filter: blur(45px);
  pointer-events: none;
}

.eni-header-glow-gold {
  left: -55px;
  top: -68px;

  background: rgba(212, 175, 55, 0.3);

  animation:
    eniHeaderGlow 5.5s ease-in-out infinite;
}

.eni-header-glow-blue {
  right: -55px;
  top: -72px;

  background: rgba(37, 99, 235, 0.3);

  animation:
    eniHeaderGlow 6.5s ease-in-out infinite reverse;
}

/* 横向扫描光 */
.eni-header-scan-line {
  position: absolute;
  z-index: -1;

  top: 0;
  bottom: 0;
  left: -140px;

  width: 110px;

  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 224, 138, 0.05),
    rgba(255, 255, 255, 0.08),
    transparent
  );

  animation:
    eniHeaderScanning 8s linear infinite;

  pointer-events: none;
}

/* =========================================================
 * 基础区域
 * ========================================================= */

.eni-header-left,
.eni-header-center,
.eni-header-right {
  position: relative;
  z-index: 5;

  display: flex;
  align-items: center;

  min-height: 50px;
}

.eni-header-left {
  justify-content: flex-start;
  min-width: 0;

  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.eni-header-center {
  justify-content: center;
  min-width: 0;
}

.eni-header-right {
  justify-content: flex-end;
}

/* =========================================================
 * LOGO 品牌区域
 * ========================================================= */

.eni-brand-shell {
  position: relative;

  /* 加宽 LOGO 外层 */
  width: 108px;
  height: 52px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 18px;

  transform: translateX(-4px);
}

/* LOGO 外围轨道 */
.eni-brand-orbit {
  position: absolute;
  inset: 1px;

  border-radius: 19px;

  border:
    1px solid rgba(255, 224, 138, 0.28);

  clip-path: polygon(
    0 0,
    58% 0,
    58% 2px,
    100% 2px,
    100% 100%,
    0 100%
  );

  animation:
    eniBrandOrbit 6s linear infinite;

  pointer-events: none;
}

.eni-brand-orbit-secondary {
  inset: 4px;

  border-color:
    rgba(59, 130, 246, 0.25);

  animation-duration: 8s;
  animation-direction: reverse;
}

/* =========================================================
 * 浅色 LOGO 方框
 * 黑色 LOGO 在这个底色上会更加清晰
 * ========================================================= */

.eni-brand-logo-box {
  position: relative;

  width: 102px;
  height: 47px;

  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 16px;

  /* 浅银白、略带蓝灰，不会显得突兀 */
  background:
    radial-gradient(
      circle at 12% 0%,
      rgba(255, 255, 255, 1),
      transparent 42%
    ),
    radial-gradient(
      circle at 100% 100%,
      rgba(191, 219, 254, 0.65),
      transparent 46%
    ),
    linear-gradient(
      145deg,
      #ffffff 0%,
      #f5f8fc 42%,
      #e7edf5 72%,
      #dce6f2 100%
    );

  border:
    1px solid rgba(255, 255, 255, 0.86);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(74, 103, 145, 0.12),
    0 9px 24px rgba(0, 0, 0, 0.3),
    0 0 22px rgba(212, 175, 55, 0.1),
    0 0 16px rgba(59, 130, 246, 0.08);

  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease;

  animation:
    eniBrandFloat 4.5s ease-in-out infinite;
}

/* LOGO 方框内部柔光 */
.eni-brand-inner-glow {
  position: absolute;
  z-index: 0;

  left: 12px;
  right: 12px;
  bottom: -15px;

  height: 26px;

  border-radius: 50%;

  background:
    rgba(59, 130, 246, 0.17);

  filter: blur(12px);

  pointer-events: none;
}

.eni-header-left:hover .eni-brand-logo-box {
  transform: translateY(-2px);

  border-color:
    rgba(255, 224, 138, 0.7);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -1px 0 rgba(74, 103, 145, 0.13),
    0 13px 30px rgba(0, 0, 0, 0.36),
    0 0 28px rgba(212, 175, 55, 0.18),
    0 0 20px rgba(59, 130, 246, 0.12);
}

/* LOGO 扫光 */
.eni-brand-shine {
  position: absolute;
  z-index: 3;

  top: -18px;
  bottom: -18px;
  left: -48px;

  width: 30px;

  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.72),
    transparent
  );

  animation:
    eniBrandShine 4.8s ease-in-out infinite;

  pointer-events: none;
}

/* LOGO 图片放大 */
.eni-brand-logo {
  position: relative;
  z-index: 2;

  width: 96px;
  height: 45px;

  object-fit: contain;
  display: block;

  /*
   * 再放大一点。
   * 即使原始 PNG 四周有透明空白，文字也会更大。
   */
  transform: scale(1.16);

  transform-origin: center;

  filter:
    contrast(1.15)
    brightness(0.78)
    drop-shadow(0 1px 1px rgba(0, 0, 0, 0.12));

  transition:
    transform 0.22s ease,
    filter 0.22s ease;
}

.eni-header-left:hover .eni-brand-logo {
  transform: scale(1.2);

  filter:
    contrast(1.2)
    brightness(0.72)
    drop-shadow(0 2px 2px rgba(0, 0, 0, 0.16));
}

.eni-header-left:active .eni-brand-logo {
  transform: scale(1.12);
}

/* LOGO 在线状态 */
.eni-brand-live {
  position: absolute;
  z-index: 8;

  right: -1px;
  bottom: -1px;

  width: 18px;
  height: 18px;

  border:
    3px solid #07101d;

  border-radius: 50%;

  background:
    rgba(34, 197, 94, 0.14);

  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.28);
}

.eni-brand-live i {
  width: 8px;
  height: 8px;

  border-radius: 50%;

  background: #22c55e;

  box-shadow:
    0 0 7px rgba(34, 197, 94, 0.95),
    0 0 15px rgba(34, 197, 94, 0.62);

  animation:
    eniStatusPulse 1.8s ease-in-out infinite;
}

/* =========================================================
 * 钱包连接区域
 * ========================================================= */

.eni-wallet-pill {
  position: relative;

  width: 100%;
  min-width: 132px;
  max-width: 220px;
  height: 48px;

  padding: 0 10px;

  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  gap: 9px;

  border-radius: 18px;

  background:
    radial-gradient(
      circle at 18% 0%,
      rgba(59, 130, 246, 0.13),
      transparent 42%
    ),
    linear-gradient(
      145deg,
      rgba(19, 30, 50, 0.92),
      rgba(7, 13, 24, 0.94)
    );

  border:
    1px solid rgba(96, 165, 250, 0.2);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    0 10px 26px rgba(0, 0, 0, 0.26),
    0 0 20px rgba(37, 99, 235, 0.05);

  box-sizing: border-box;
  cursor: pointer;
  user-select: none;

  -webkit-tap-highlight-color: transparent;

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.eni-wallet-pill::after {
  content: "";

  position: absolute;

  top: 0;
  left: 18%;
  right: 18%;

  height: 1px;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(147, 197, 253, 0.65),
    transparent
  );

  opacity: 0.55;
}

.eni-wallet-pill.connected {
  border-color:
    rgba(255, 224, 138, 0.27);

  background:
    radial-gradient(
      circle at 16% 0%,
      rgba(212, 175, 55, 0.12),
      transparent 42%
    ),
    radial-gradient(
      circle at 100% 100%,
      rgba(37, 99, 235, 0.13),
      transparent 40%
    ),
    linear-gradient(
      145deg,
      rgba(22, 30, 47, 0.95),
      rgba(7, 13, 24, 0.96)
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 12px 28px rgba(0, 0, 0, 0.3),
    0 0 24px rgba(212, 175, 55, 0.06);
}

.eni-wallet-pill:hover {
  transform: translateY(-2px);

  border-color:
    rgba(255, 224, 138, 0.34);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.09),
    0 15px 32px rgba(0, 0, 0, 0.35),
    0 0 28px rgba(37, 99, 235, 0.1);
}

.eni-wallet-pill:active {
  transform: scale(0.985);
}

/* 钱包内部扫光 */
.eni-wallet-shine {
  position: absolute;
  z-index: 0;

  top: -20px;
  bottom: -20px;
  left: -55px;

  width: 34px;

  transform: skewX(-22deg);

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.11),
    transparent
  );

  animation:
    eniWalletShine 5.8s ease-in-out infinite;

  pointer-events: none;
}

/* 钱包图标区域 */
.eni-wallet-logo-wrap {
  position: relative;
  z-index: 2;

  width: 33px;
  height: 33px;
  flex: 0 0 33px;

  border-radius: 12px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: visible;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.035)
    );

  border:
    1px solid rgba(255, 255, 255, 0.1);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 5px 14px rgba(0, 0, 0, 0.24);
}

.eni-wallet-ring {
  position: absolute;
  inset: -3px;

  border-radius: 14px;

  border:
    1px dashed rgba(96, 165, 250, 0.28);

  animation:
    eniWalletRingRotate 12s linear infinite;

  pointer-events: none;
}

.connected .eni-wallet-ring {
  border-color:
    rgba(255, 224, 138, 0.3);
}

.eni-wallet-logo {
  position: relative;
  z-index: 2;

  width: 24px;
  height: 24px;

  object-fit: contain;
  display: block;

  border-radius: 7px;
}

/* 未连接钱包加号 */
.eni-wallet-badge {
  position: relative;
  z-index: 2;

  width: 23px;
  height: 23px;

  border-radius: 8px;

  color: #07101c;

  background:
    radial-gradient(
      circle at 28% 20%,
      #ffffff,
      transparent 30%
    ),
    linear-gradient(
      135deg,
      #60a5fa 0%,
      #93c5fd 52%,
      #ffe08a 100%
    );

  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow:
    0 0 14px rgba(59, 130, 246, 0.32);

  animation:
    eniWalletBadgePulse 2.8s ease-in-out infinite;
}

.eni-wallet-badge span {
  display: block;

  margin-top: -1px;

  font-size: 18px;
  font-weight: 900;
  line-height: 1;
}

/* 钱包在线状态 */
.eni-wallet-online-dot {
  position: absolute;
  z-index: 4;

  right: -3px;
  bottom: -3px;

  width: 10px;
  height: 10px;

  border:
    2px solid #091220;

  border-radius: 50%;

  background: #22c55e;

  box-shadow:
    0 0 10px rgba(34, 197, 94, 0.9);

  animation:
    eniStatusPulse 1.7s ease-in-out infinite;
}

/* 钱包文字 */
.eni-wallet-content {
  position: relative;
  z-index: 2;

  min-width: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
}

.eni-wallet-label {
  overflow: hidden;

  color: #687a98;

  font-family:
    SFMono-Regular,
    Consolas,
    "Liberation Mono",
    monospace;

  font-size: 8px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.8px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.connected .eni-wallet-label {
  color: #75f3a3;
}

.eni-wallet-text {
  min-width: 0;
  margin-top: 5px;

  overflow: hidden;

  color: #eaf1ff;

  font-size: 12px;
  font-weight: 850;
  line-height: 1.15;
  letter-spacing: 0.1px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.connected .eni-wallet-text {
  color: #fff7d4;
}

/* 钱包右侧箭头 */
.eni-wallet-arrow {
  position: relative;
  z-index: 2;

  width: 15px;
  height: 15px;
  flex: 0 0 15px;

  fill: none;
  stroke: #6f82a2;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;

  transition:
    transform 0.2s ease,
    stroke 0.2s ease;
}

.eni-wallet-pill:hover .eni-wallet-arrow {
  transform: translateX(2px);
  stroke: #ffe08a;
}

/* =========================================================
 * 右侧菜单按钮
 * ========================================================= */

.eni-menu-btn {
  position: relative;

  width: 44px;
  height: 44px;
  padding: 0;

  overflow: hidden;

  border-radius: 15px;

  border:
    1px solid rgba(96, 165, 250, 0.2);

  background:
    radial-gradient(
      circle at 30% 15%,
      rgba(59, 130, 246, 0.16),
      transparent 42%
    ),
    linear-gradient(
      145deg,
      rgba(19, 30, 50, 0.92),
      rgba(7, 13, 24, 0.96)
    );

  display: inline-flex;
  align-items: center;
  justify-content: center;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    0 9px 23px rgba(0, 0, 0, 0.28);

  outline: none;
  cursor: pointer;

  -webkit-tap-highlight-color: transparent;

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.eni-menu-btn::before {
  content: "";

  position: absolute;
  inset: 5px;

  border-radius: 11px;

  border:
    1px solid rgba(255, 255, 255, 0.045);

  pointer-events: none;
}

.eni-menu-btn-glow {
  position: absolute;

  width: 32px;
  height: 32px;

  border-radius: 50%;

  background:
    rgba(37, 99, 235, 0.15);

  filter: blur(13px);

  animation:
    eniMenuGlow 3.4s ease-in-out infinite;
}

.eni-menu-btn:hover {
  transform:
    translateY(-2px)
    rotate(2deg);

  border-color:
    rgba(255, 224, 138, 0.34);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.09),
    0 13px 29px rgba(0, 0, 0, 0.34),
    0 0 22px rgba(37, 99, 235, 0.1);
}

.eni-menu-btn:active {
  transform: scale(0.95);
}

.eni-menu-svg {
  position: relative;
  z-index: 2;

  width: 20px;
  height: 20px;

  fill: none;
  stroke: #d7e5ff;
  stroke-width: 2.25;
  stroke-linecap: round;
  stroke-linejoin: round;

  filter:
    drop-shadow(0 0 5px rgba(59, 130, 246, 0.25));

  transition:
    stroke 0.2s ease,
    transform 0.2s ease;
}

.eni-menu-btn:hover .eni-menu-svg {
  stroke: #ffe08a;
  transform: scale(1.05);
}

.eni-menu-svg path:first-child {
  stroke: #ffe08a;
}

.eni-menu-svg path:nth-child(2) {
  stroke: #93c5fd;
}

.eni-menu-svg path:last-child {
  stroke: #dbeafe;
}

/* =========================================================
 * 动画
 * ========================================================= */

@keyframes eniHeaderGlow {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(0.9);
  }

  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes eniHeaderScanning {
  0% {
    left: -150px;
    opacity: 0;
  }

  12% {
    opacity: 1;
  }

  55% {
    opacity: 0.9;
  }

  70%,
  100% {
    left: calc(100% + 150px);
    opacity: 0;
  }
}

@keyframes eniBrandFloat {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-2px);
  }
}

@keyframes eniBrandOrbit {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes eniBrandShine {
  0%,
  24% {
    left: -48px;
    opacity: 0;
  }

  35% {
    opacity: 1;
  }

  57% {
    left: 135px;
    opacity: 0;
  }

  100% {
    left: 135px;
    opacity: 0;
  }
}

@keyframes eniWalletShine {
  0%,
  18% {
    left: -55px;
    opacity: 0;
  }

  27% {
    opacity: 1;
  }

  48% {
    left: calc(100% + 55px);
    opacity: 0;
  }

  100% {
    left: calc(100% + 55px);
    opacity: 0;
  }
}

@keyframes eniWalletRingRotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes eniWalletBadgePulse {
  0%,
  100% {
    transform: scale(0.94);

    box-shadow:
      0 0 10px rgba(59, 130, 246, 0.24);
  }

  50% {
    transform: scale(1.04);

    box-shadow:
      0 0 18px rgba(59, 130, 246, 0.48);
  }
}

@keyframes eniStatusPulse {
  0%,
  100% {
    opacity: 0.55;
    transform: scale(0.82);
  }

  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes eniMenuGlow {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.8);
  }

  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

/* =========================================================
 * 手机适配
 * ========================================================= */

@media (max-width: 375px) {
  .eni-app-header {
    min-height: 74px;

    padding:
      calc(7px + env(safe-area-inset-top))
      9px
      7px;

    /* 手机端也给 LOGO 留出足够宽度 */
    grid-template-columns:
      101px
      minmax(0, 1fr)
      42px;

    column-gap: 6px;
  }

  .eni-brand-shell {
    width: 98px;
    height: 49px;

    transform: translateX(-4px);
  }

  .eni-brand-logo-box {
    width: 93px;
    height: 44px;
    border-radius: 15px;
  }

  .eni-brand-logo {
    width: 88px;
    height: 42px;

    transform: scale(1.15);
  }

  .eni-header-left:hover .eni-brand-logo {
    transform: scale(1.18);
  }

  .eni-brand-live {
    width: 17px;
    height: 17px;
  }

  .eni-wallet-pill {
    min-width: 0;
    max-width: 182px;

    height: 45px;

    padding: 0 8px;
    gap: 7px;

    border-radius: 16px;
  }

  .eni-wallet-logo-wrap {
    width: 30px;
    height: 30px;
    flex-basis: 30px;

    border-radius: 10px;
  }

  .eni-wallet-logo {
    width: 22px;
    height: 22px;
  }

  .eni-wallet-badge {
    width: 21px;
    height: 21px;
  }

  .eni-wallet-label {
    font-size: 7px;
  }

  .eni-wallet-text {
    margin-top: 4px;
    font-size: 11px;
  }

  .eni-wallet-arrow {
    width: 13px;
    height: 13px;
    flex-basis: 13px;
  }

  .eni-menu-btn {
    width: 40px;
    height: 40px;

    border-radius: 14px;
  }

  .eni-menu-svg {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 350px) {
  .eni-app-header {
    grid-template-columns:
      92px
      minmax(0, 1fr)
      39px;

    padding-left: 7px;
    padding-right: 7px;

    column-gap: 5px;
  }

  .eni-brand-shell {
    width: 90px;
  }

  .eni-brand-logo-box {
    width: 86px;
    height: 42px;
  }

  .eni-brand-logo {
    width: 82px;
    height: 40px;

    transform: scale(1.12);
  }

  .eni-wallet-pill {
    padding: 0 7px;
    gap: 6px;
  }

  .eni-wallet-logo-wrap {
    width: 28px;
    height: 28px;
    flex-basis: 28px;
  }

  .eni-wallet-label {
    display: none;
  }

  .eni-wallet-text {
    margin-top: 0;
    font-size: 10px;
  }

  .eni-wallet-arrow {
    display: none;
  }

  .eni-menu-btn {
    width: 38px;
    height: 38px;
  }
}

/* =========================================================
 * 用户系统启用“减少动画”时停止动画
 * ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .eni-header-glow,
  .eni-header-scan-line,
  .eni-brand-orbit,
  .eni-brand-logo-box,
  .eni-brand-shine,
  .eni-brand-live i,
  .eni-wallet-shine,
  .eni-wallet-ring,
  .eni-wallet-badge,
  .eni-wallet-online-dot,
  .eni-menu-btn-glow {
    animation: none !important;
  }
}
</style>